import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import React from 'react';

import {
  createGoal,
  copyGoal,
  updateGoal,
  deleteGoal,
  reorderGoals,
} from './data';
import { useNotification } from '../../../shared/Notification/useNotification';
import UndoDeleteNotification from '../components/UndoDeleteNotification';

export const useCreateGoalMutation = () => {
  const { t } = useTranslation('goals');
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const { mutate: createGoalMutation, isLoading: isCreating } = useMutation(
    createGoal,
    {
      onSuccess: () => {
        showSuccess({
          content: t('goalNotifications.created', {
            defaultValue: 'Goal created successfully!',
          }),
        });

        // Invalidate and refetch goals to ensure proper ordering
        queryClient.invalidateQueries(['goals']);
        queryClient.refetchQueries(['goals']);
      },
    },
  );

  return { createGoalMutation, isCreating };
};

export const useCopyGoalMutation = () => {
  const { t } = useTranslation('goals');
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();

  const { mutate: copyGoalMutation, isLoading: isCopying } = useMutation(
    copyGoal,
    {
      onSuccess: () => {
        showSuccess({
          content: t('goalNotifications.copied', {
            defaultValue: 'Goal copied successfully!',
          }),
        });

        // Invalidate and refetch goals to ensure proper ordering
        queryClient.invalidateQueries(['goals']);
        queryClient.refetchQueries(['goals']);
      },
    },
  );

  return { copyGoalMutation, isCopying };
};

export const useUpdateGoalMutation = () => {
  const queryClient = useQueryClient();

  const { mutate: updateGoalMutation, isLoading: isUpdating } = useMutation(
    updateGoal,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['goals']);
      },
    },
  );

  return { updateGoalMutation, isUpdating };
};

export const useDeleteGoalMutation = () => {
  const queryClient = useQueryClient();
  const { showSuccess, handleClose } = useNotification();
  const pendingDeletions = useRef(new Map());

  const { mutate: deleteGoalMutation, isLoading: isDeleting } = useMutation(
    deleteGoal,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['goals']);
      },
    },
  );

  const initiateDeleteWithUndo = goal => {
    const notificationId = `delete-goal-${goal.id}`;

    // Clear any existing timeout for this goal
    if (pendingDeletions.current.has(goal.id)) {
      clearTimeout(pendingDeletions.current.get(goal.id).timeoutId);
      handleClose(pendingDeletions.current.get(goal.id).notificationId);
    }

    // Store the original index before removing
    const currentGoals = queryClient.getQueryData(['goals']) || [];
    const originalIndex = currentGoals.findIndex(g => g.id === goal.id);

    // Immediately remove the goal from the cache to hide it from UI
    queryClient.setQueryData(['goals'], oldGoals => {
      if (!oldGoals) return oldGoals;
      return oldGoals.filter(g => g.id !== goal.id);
    });

    const handleUndo = () => {
      if (pendingDeletions.current.has(goal.id)) {
        clearTimeout(pendingDeletions.current.get(goal.id).timeoutId);
        pendingDeletions.current.delete(goal.id);

        // Restore the goal to its exact original position
        queryClient.setQueryData(['goals'], oldGoals => {
          if (!oldGoals) return [goal];

          // Check if goal already exists (in case of race conditions)
          const goalExists = oldGoals.some(g => g.id === goal.id);
          if (goalExists) return oldGoals;

          // Convert goal back to API format for consistency
          const goalToRestore = {
            id: goal.id,
            name: goal.goal,
            plan_year: goal.planYear,
            position: goal.position,
            created_at: goal.createdAt,
          };

          // Insert at the original index (or at the end if index is beyond current length)
          const insertIndex = Math.min(originalIndex, oldGoals.length);
          return [
            ...oldGoals.slice(0, insertIndex),
            goalToRestore,
            ...oldGoals.slice(insertIndex),
          ];
        });
      }
    };

    showSuccess({
      content: ({ handleClose: closeNotification }) =>
        React.createElement(UndoDeleteNotification, {
          goalName: goal.goal,
          onUndo: handleUndo,
          handleClose: closeNotification,
        }),
      title: 'Goal deleted successfully!',
      duration: 7000,
      id: notificationId,
    });

    const timeoutId = setTimeout(() => {
      deleteGoalMutation(goal.id);
      handleClose(notificationId);
      pendingDeletions.current.delete(goal.id);
    }, 7000);

    pendingDeletions.current.set(goal.id, {
      timeoutId,
      notificationId,
      originalGoal: goal,
      originalIndex,
    });
  };

  return {
    deleteGoalMutation: initiateDeleteWithUndo,
    isDeleting,
    immediateDeleteGoalMutation: deleteGoalMutation, // Keep the original for emergency use
  };
};

export const useReorderGoalsMutation = () => {
  const queryClient = useQueryClient();

  const { mutate: reorderGoalsMutation, isLoading: isReordering } = useMutation(
    reorderGoals,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['goals']);
      },
    },
  );

  return { reorderGoalsMutation, isReordering };
};
